import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Pagination, Response } from '@ui/src';
import { PspPartnerItem, PspPartnersValidator } from '../../types';
import { pspApi } from '../services';

import { BankRoutes } from '../../../../routes';

export type Filters = Record<string, string | string[]>;

export interface BankAndPpsListState {
  isLoading: boolean;
  requestInProgress: boolean;
  values?: PspPartnerItem[];
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  pagination?: Pagination;
  validators?: PspPartnersValidator;
  errorsList?: string;
}

const initialState: BankAndPpsListState = {
  isLoading: false,
  values: undefined,
  sort: undefined,
  requestInProgress: false,
  filters: undefined,
};

export const bankAndPsplistSlice = createSlice({
  name: 'bankAndPspList',
  initialState,
  reducers: {
    clearBanksState: (state) => {
      state.values = undefined;
      state.errorsList = undefined;
      state.pagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      pspApi.endpoints.getPspPartnersValidators.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnersValidator }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getConfigurationPspPartners.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getConfigurationPspPartners.matchRejected,
      (state, action: any) => {
        state.values = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getConfigurationPspPartners.matchFulfilled,
      (state, action: PayloadAction<Response<PspPartnerItem>>) => {
        state.values = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(BankRoutes.BANK_AND_PSP_PAGE, {
            bankId: item.node.id,
          }),
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.createPspPartner.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        const nextPspPartners = ([] as PspPartnerItem[]).concat(
          state.values as PspPartnerItem[],
        );

        nextPspPartners.unshift({
          ...action.payload.data,
          url: generatePath(BankRoutes.BANK_AND_PSP_PAGE, {
            bankId: action.payload.data.id,
          }),
        });

        state.values = nextPspPartners;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspPartner.matchFulfilled,
      (state, action: PayloadAction<{ data: PspPartnerItem }>) => {
        if (state.values) {
          state.values = state.values.map((item) => {
            let result = { ...item };

            if (item.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                url: generatePath(BankRoutes.BANK_AND_PSP_PAGE, {
                  bankId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        }
      },
    );
  },
});

export const { clearBanksState } = bankAndPsplistSlice.actions;

export default bankAndPsplistSlice.reducer;
